import { Badge } from '@ui';
import { Booking } from 'components/EventCalendar/types';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

interface Props {
    booking: Booking;
}

/** One chip per row, always on the same line: declined beats cancelled beats unread beats read.
 * The detail underneath belongs to whichever chip won.
 *
 * declined has to come first because a decline writes cancelled_at too. */
export const StatusComponent = ({ booking }: Props) => {
    const { t } = useTranslation('bookings');

    const [color, label, detail] = booking.declined_at
        ? ([
              'error',
              t('booking.list.badges.declined'),
              [moment(booking.declined_at).format('DD.MM.YY'), booking.cancellation_reason]
                  .filter(Boolean)
                  .join(' · ')
          ] as const)
        : booking.approval_status === 'pending'
          ? ([
                'warning',
                t('booking.list.badges.pending'),
                booking.requested_at ? moment(booking.requested_at).format('DD.MM.YY') : ''
            ] as const)
          : booking.approval_status === 'awaiting_payment'
            ? ([
                  'warning',
                  t('booking.list.badges.awaitingPayment'),
                  booking.payment_due_at ? moment(booking.payment_due_at).format('DD.MM.YY HH:mm') : ''
              ] as const)
            : booking.cancelled_at
              ? ([
                    'error',
                    t('booking.list.badges.cancelled'),
                    [moment(booking.cancelled_at).format('DD.MM.YY'), booking.cancellation_reason]
                        .filter(Boolean)
                        .join(' · ')
                ] as const)
              : booking.read_at
                ? ([
                      'success',
                      t('booking.list.badges.read'),
                      [moment(booking.read_at).format('DD.MM.YY'), booking.read_by].filter(Boolean).join(' · ')
                  ] as const)
                : (['warning', t('booking.list.badges.unread'), ''] as const);

    return (
        <div className="tw-flex tw-min-w-0 tw-flex-col tw-items-start tw-gap-1">
            <Badge flat size="xs" color={color}>
                {label}
            </Badge>
            {detail && (
                <span className="tw-w-full tw-truncate tw-text-xs tw-leading-4 tw-text-accents-6" title={detail}>
                    {detail}
                </span>
            )}
        </div>
    );
};
